import { api } from "../api";
import { openModal, closeModal, modalBody } from "../components/modal";
import { openCsvImportModal } from "../components/csv-import-modal";
import { renderDocumentsTab, attachDocumentsListeners } from "./student-documents";
import { canWrite, writeOnly, readOnlyNotice, lockInputs } from "../utils/permissions";
import type { Student, SchoolClass, StudentHours, StudentHoursResponse, AdminStudentAssignment, Department, StudentPreferences, SchuelerSteckbrief, Kreisverband } from "../types";
import { esc } from "../utils/html";

let students: Student[] = [];
let classes: SchoolClass[] = [];
let filterClass: number | undefined;
let searchQuery = "";
let currentEl: HTMLElement | null = null;
let schuelerPortalEnabled = false;
let planungEnabled = false;
let stundenplanEnabled = false;
let drkKreisverbandEnabled = false;
let kreisverbaende: Kreisverband[] = [];
let _isSchulAdmin = false;

/** Wird von app.ts nach dem Login aufgerufen, um Feature-Kontext zu übergeben */
export function setUserContext(features: string[], isSchulAdmin: boolean) {
  schuelerPortalEnabled = features.includes("schuelerPortal");
  planungEnabled = features.includes("planung");
  stundenplanEnabled = features.includes("stundenplan");
  drkKreisverbandEnabled = features.includes("drkKreisverband");
  _isSchulAdmin = isSchulAdmin;
}

async function load() {
  [students, classes] = await Promise.all([api.students.list(filterClass), api.classes.list()]);
  // Kreisverband-Liste nur für freigeschaltete Schulen laden (fürs Dropdown)
  if (drkKreisverbandEnabled) {
    try { kreisverbaende = await api.kreisverbaende.list(); } catch { kreisverbaende = []; }
  }
}

/** Form-Group mit Kreisverband-Dropdown; leer, wenn Feature nicht freigeschaltet. */
function kreisverbandFieldHtml(selectId: string, selectedId?: number | null): string {
  if (!drkKreisverbandEnabled) return "";
  const opts = kreisverbaende
    .map(k => `<option value="${k.id}" ${selectedId === k.id ? "selected" : ""}>${esc(k.name)}</option>`)
    .join("");
  return `<div class="form-group"><label>DRK Kreisverband</label><select id="${selectId}"><option value="">– Keiner –</option>${opts}</select></div>`;
}

// Global handlers for onclick attributes
(window as any).__sDetail = async (id: number) => {
  try {
    const s = students.find(st => st.id == id);
    if (s) openDetail(s);
  } catch (err: any) { alert("Fehler: " + err.message); }
};
(window as any).__sDel = async (id: number) => {
  try {
    if (!confirm("Schüler wirklich löschen?")) return;
    await api.students.delete(id);
    await load();
    if (currentEl) renderTable(currentEl);
  } catch (err: any) { alert("Fehler: " + err.message); }
};

(window as any).__sToggleLogin = async (id: number, currentEnabled: boolean) => {
  try {
    const s = students.find(st => st.id == id);
    if (!s) return;

    if (currentEnabled) {
      // Deaktivieren
      if (!confirm(`Login für ${s.firstName} ${s.lastName} wirklich deaktivieren?`)) return;
      await api.students.toggleLogin(id, false);
      await load();
      if (currentEl) renderTable(currentEl);
    } else {
      // Aktivieren – Modal öffnen
      openLoginEnableModal(s);
    }
  } catch (err: any) { alert("Fehler: " + err.message); }
};

(window as any).__sResetPw = async (id: number) => {
  try {
    if (!confirm("Passwort-Reset-E-Mail an den Schüler senden?")) return;
    await api.students.resetPassword(id);
    alert("Die Passwort-Reset-E-Mail wurde versandt.");
  } catch (err: any) { alert("Fehler: " + err.message); }
};

function openLoginEnableModal(s: Student) {
  if (!s.firstName?.trim() || !s.lastName?.trim()) {
    alert("Vor- und Nachname des Schülers müssen im Profil gefüllt sein, bevor der Login aktiviert werden kann.");
    return;
  }
  const hasEmail = !!s.email;
  openModal(`Login aktivieren: ${esc(s.firstName)} ${esc(s.lastName)}`, `
    <p style="font-size:.9rem;color:#374151;margin-bottom:1rem">
      Mit dem Login erhält der Schüler Zugang zum Schüler-Portal und kann seine Planung einsehen.
    </p>
    <div class="form-group">
      <label>E-Mail-Adresse (Benutzername) *</label>
      <input id="le-email" type="email" value="${esc(s.email || "")}"
        placeholder="E-Mail-Adresse eingeben"
        style="background:${hasEmail ? "#f9fafb" : "#fff"}" />
      ${hasEmail ? `<small style="color:#6b7280">Bereits hinterlegt – kann angepasst werden.</small>` : `<small style="color:#dc2626">Keine E-Mail hinterlegt – bitte eingeben.</small>`}
    </div>
    <div class="alert" style="background:#f9fafb;border:1px solid #e5e7eb;border-radius:6px;padding:.75rem 1rem;font-size:.875rem;margin-top:1rem">
      Je nach Einstellung deiner Schule bekommt der Schüler eine Einladung per E-Mail
      und vergibt sein Passwort selbst, oder dir wird hier ein Erstkennwort angezeigt.
    </div>
    <div class="form-actions">
      <button class="btn btn-secondary" id="le-cancel">Abbrechen</button>
      <button class="btn btn-primary" id="le-save">Login aktivieren</button>
    </div>`);

  const mb = modalBody();
  mb.querySelector("#le-cancel")!.addEventListener("click", closeModal);
  mb.querySelector("#le-save")!.addEventListener("click", async () => {
    const email = (mb.querySelector("#le-email") as HTMLInputElement).value.trim();
    if (!email) { alert("E-Mail-Adresse ist erforderlich."); return; }

    // Sofort Spinner anzeigen
    document.getElementById("modal-title")!.textContent = "Login wird aktiviert…";
    mb.innerHTML = `
      <div style="text-align:center;padding:2rem 1rem">
        <div class="spinner" style="margin:0 auto 1.5rem"></div>
        <p style="color:#6b7280;font-size:.95rem">Login wird aktiviert…</p>
      </div>`;

    try {
      const result = await api.students.toggleLogin(s.id, true, email);
      const password = result.password || "";
      // Einladung verschickt oder eingereiht: kein Kennwort unterwegs, der Schüler
      // vergibt es selbst.
      const queued = result.mode === "invite" && result.emailQueued === true;
      const invited = queued || (result.mode === "invite" && result.emailSent === true);

      const usernameBlock = `
        <div class="form-group">
          <label>Benutzername (E-Mail)</label>
          <input type="text" value="${esc(email)}" readonly style="background:#f9fafb;font-family:monospace" />
        </div>`;

      // Notfallweg: Zugang steht, aber die Einladung ging nicht raus.
      const passwordBlock = `
        <div class="form-group">
          <label>Erstkennwort (nur jetzt sichtbar!)</label>
          <div style="display:flex;gap:.5rem">
            <input id="pw-display" type="text" value="${esc(password)}" readonly
              style="background:#f0fdf4;border-color:#86efac;font-family:monospace;font-size:1rem;font-weight:600;letter-spacing:.05em;flex:1" />
            <button class="btn btn-secondary" onclick="navigator.clipboard.writeText(document.getElementById('pw-display').value).then(()=>this.textContent='✓ Kopiert').catch(()=>{})" style="white-space:nowrap">Kopieren</button>
          </div>
          <small style="color:#dc2626;font-weight:500">Dieses Kennwort wird nicht erneut angezeigt!</small>
        </div>`;

      let statusBlock: string;
      if (invited) {
        statusBlock = `
          <div class="alert" style="background:#f0fdf4;border:1px solid #86efac;border-radius:6px;padding:.75rem 1rem;font-size:.875rem;margin-bottom:1rem">
            Eine Einladung ${queued ? "wird in Kürze" : "wurde"} an <strong>${esc(email)}</strong> verschickt. Der Schüler vergibt
            sein Passwort über den Link in der E-Mail selbst. Kommt die Mail nicht an, kannst du
            sie über "Passwort zurücksetzen" in der Schülerliste erneut senden.
          </div>`;
      } else if (result.mode === "invite") {
        statusBlock = `
          <div class="alert" style="background:#fef2f2;border:1px solid #fca5a5;border-radius:6px;padding:.75rem 1rem;font-size:.875rem;margin-bottom:1rem">
            Der Zugang wurde eingerichtet, die Einladung konnte aber <strong>nicht versendet</strong> werden.
            Bitte gib dem Schüler das folgende Erstkennwort persönlich weiter.
          </div>`;
      } else {
        statusBlock = `
          <p style="font-size:.9rem;color:#374151;margin-bottom:1rem">
            Der Login für <strong>${esc(s.firstName)} ${esc(s.lastName)}</strong> wurde aktiviert.
          </p>`;
      }

      document.getElementById("modal-title")!.textContent = invited
        ? (queued ? "Login aktiviert, Einladung folgt" : "Login aktiviert, Einladung versandt")
        : "Login aktiviert, Kennwort";
      mb.innerHTML = `
        ${statusBlock}
        ${usernameBlock}
        ${invited ? "" : passwordBlock}
        <div class="form-actions">
          <button class="btn btn-primary" id="pw-close">Schließen</button>
        </div>`;

      mb.querySelector("#pw-close")!.addEventListener("click", async () => {
        closeModal();
        await load();
        if (currentEl) renderTable(currentEl);
      });
    } catch (err: any) {
      document.getElementById("modal-title")!.textContent = "Fehler";
      mb.innerHTML = `
        <p style="color:#dc2626;margin-bottom:1rem">Fehler: ${esc(err.message)}</p>
        <div class="form-actions">
          <button class="btn btn-secondary" id="err-close">Schließen</button>
        </div>`;
      mb.querySelector("#err-close")!.addEventListener("click", closeModal);
    }
  });
}

function renderTable(el: HTMLElement) {
  const tbody = el.querySelector("tbody")!;
  const baseColspan = (schuelerPortalEnabled ? 8 : 7) - (canWrite() ? 0 : 1);
  const thead = el.querySelector("thead tr")!;
  const loginHeaderCell = schuelerPortalEnabled
    ? `<th style="text-align:center">Schüler-Portal</th>`
    : "";
  thead.innerHTML = `${writeOnly(`<th style="width:36px"><input type="checkbox" id="sel-all-s" style="width:16px;height:16px;accent-color:var(--primary);cursor:pointer" /></th>`)}<th>Vorname</th><th>Nachname</th><th>PLZ</th><th>Ort</th><th>Klasse</th><th style="width:50px;text-align:center">Geo</th>${loginHeaderCell}`;

  const q = searchQuery.trim().toLowerCase();
  const filtered = q
    ? students.filter(s => [s.firstName, s.lastName, s.plz, s.ort, s.className].some(v => (v || "").toLowerCase().includes(q)))
    : students;

  if (filtered.length === 0) {
    tbody.innerHTML = `<tr><td colspan="${baseColspan}" class="empty-state">${q ? "Keine Treffer" : "Keine Schüler vorhanden"}</td></tr>`;
    return;
  }
  tbody.innerHTML = filtered.map(s => `
    <tr style="cursor:pointer" onclick="window.__sDetail(${s.id})">
      ${writeOnly(`<td onclick="event.stopPropagation()"><input type="checkbox" class="row-sel" data-id="${s.id}" onchange="window.__sUpdateSel()" /></td>`)}
      <td style="font-weight:500">${esc(s.firstName)}</td><td>${esc(s.lastName)}</td>
      <td class="text-muted">${s.plz || ""}</td><td class="text-muted">${s.ort || ""}</td>
      <td class="text-muted">${s.className || "–"}</td><td class="text-center">${studentGeoIcon(s)}</td>
      ${schuelerPortalEnabled ? `
      <td style="text-align:center">
        ${s.loginEnabled
          ? `<span class="badge badge-green">Aktiv</span>`
          : `<span style="color:var(--gray-400)">–</span>`}
      </td>` : ""}
    </tr>`).join("");

  // Auswahl-Checkbox state updaten (Button ist statisch in filter-bar, daher dynamisch per querySelector)
  (window as any).__sUpdateSel = () => {
    const boxes = [...el.querySelectorAll<HTMLInputElement>(".row-sel")];
    const checked = boxes.filter(b => b.checked);
    const selAll = el.querySelector<HTMLInputElement>("#sel-all-s");
    const delBtn = el.querySelector<HTMLButtonElement>("#btn-del-sel-s");
    if (selAll) { selAll.checked = checked.length === boxes.length && boxes.length > 0; selAll.indeterminate = checked.length > 0 && checked.length < boxes.length; }
    if (delBtn) delBtn.disabled = checked.length === 0;
    const portalBtn = el.querySelector<HTMLButtonElement>("#btn-portal-sel");
    if (portalBtn) portalBtn.disabled = checked.length === 0;
  };
  const selAllS = el.querySelector<HTMLInputElement>("#sel-all-s");
  if (selAllS) {
    selAllS.addEventListener("change", () => {
      el.querySelectorAll<HTMLInputElement>(".row-sel").forEach(b => b.checked = selAllS.checked);
      (window as any).__sUpdateSel();
    });
  }

}

function renderProgressCircle(completed: number, planned: number, required: number, color: string): string {
  const pctCompleted = required > 0 ? Math.min(completed / required, 1) : 0;
  const pctTotal = required > 0 ? Math.min((completed + planned) / required, 1) : 0;
  const pctDisplay = Math.round(pctCompleted * 100);
  const r = 36;
  const circ = 2 * Math.PI * r;
  const offsetTotal = circ - pctTotal * circ;
  const offsetCompleted = circ - pctCompleted * circ;
  return `<svg width="90" height="90" viewBox="0 0 90 90">
    <circle cx="45" cy="45" r="${r}" fill="none" stroke="#e5e7eb" stroke-width="8" />
    ${pctTotal > 0 ? `<circle cx="45" cy="45" r="${r}" fill="none" stroke="${color}" stroke-width="8" opacity="0.35"
      stroke-dasharray="${circ}" stroke-dashoffset="${offsetTotal}"
      stroke-linecap="round" transform="rotate(-90 45 45)" />` : ""}
    ${pctCompleted > 0 ? `<circle cx="45" cy="45" r="${r}" fill="none" stroke="${color}" stroke-width="8"
      stroke-dasharray="${circ}" stroke-dashoffset="${offsetCompleted}"
      stroke-linecap="round" transform="rotate(-90 45 45)" />` : ""}
    <text x="45" y="45" text-anchor="middle" dominant-baseline="central"
      font-size="16" font-weight="600" fill="#1f2937">${pctDisplay}%</text>
  </svg>`;
}

function lightenColor(hex: string): string {
  const r = parseInt(hex.slice(1, 3), 16) || 0;
  const g = parseInt(hex.slice(3, 5), 16) || 0;
  const b = parseInt(hex.slice(5, 7), 16) || 0;
  return `rgb(${Math.round(r * 0.5 + 255 * 0.5)},${Math.round(g * 0.5 + 255 * 0.5)},${Math.round(b * 0.5 + 255 * 0.5)})`;
}

function renderProgressBar(h: StudentHours): string {
  const target = h.targetHours > 0 ? h.targetHours : h.requiredHours;
  const pctCompleted = target > 0 ? Math.min(h.completedHours / target, 1) : 0;
  const pctTotal = target > 0 ? Math.min((h.completedHours + h.plannedHours) / target, 1) : 0;
  const lightColor = lightenColor(h.color);

  return `
    <div class="progress-bar-row" data-dept="${esc(h.department)}">
      <div class="progress-bar-label" title="${esc(h.nameDe)}">${esc(h.nameDe)}</div>
      <div class="progress-bar-track">
        ${pctTotal > 0 ? `<div class="progress-bar-fill" style="width:${pctTotal * 100}%;background:${lightColor}"></div>` : ""}
        ${pctCompleted > 0 ? `<div class="progress-bar-fill" style="width:${pctCompleted * 100}%;background:${h.color}"></div>` : ""}
      </div>
      <div class="progress-bar-text">
        <span style="color:${h.color};font-weight:600">${h.completedHours} Std.</span>${h.plannedHours > 0 ? ` <span style="color:#9ca3af">+${h.plannedHours} geplant</span>` : ""} <span style="color:#9ca3af">/ ${target} Ziel</span>
      </div>
    </div>`;
}

function renderPoolEditor(hoursData: StudentHoursResponse, studentId: number): string {
  const { hours, groupPoolHours, hasStudentOverride } = hoursData;
  if (!groupPoolHours || groupPoolHours <= 0) return "";

  const distributed = hours.reduce((sum, h) => sum + (h.poolHours ?? 0), 0);
  const overrideLabel = hasStudentOverride
    ? `<span class="badge badge-blue" style="margin-left:.375rem;font-size:.7rem">Individuell</span>`
    : "";

  const rows = hours.map(h => `
    <div style="display:flex;align-items:center;gap:.5rem;padding:.25rem 0">
      <span style="display:inline-block;width:.75rem;height:.75rem;border-radius:.125rem;background:${esc(h.color)};flex-shrink:0"></span>
      <span style="flex:1;font-size:.875rem">${esc(h.nameDe)}</span>
      <input type="number" class="student-pool-input" data-dept-id="${esc(h.department)}"
        value="${h.poolHours ?? 0}" min="0"
        style="width:80px;padding:.2rem .4rem;border:1px solid var(--gray-300);border-radius:.25rem;font-size:.875rem;text-align:right" />
      <span style="font-size:.8125rem;color:var(--gray-500);width:1.5rem">h</span>
    </div>`).join("");

  return `
    <details id="student-pool-section" style="margin-top:.75rem;border:1px solid var(--gray-200);border-radius:.375rem;background:#f8fafc">
      <summary style="cursor:pointer;list-style:none;display:flex;align-items:center;justify-content:space-between;padding:.625rem .875rem;font-size:.8125rem;font-weight:600;color:var(--gray-700);user-select:none">
        <span>Zu verteilende Stunden anpassen${overrideLabel}</span>
        <span id="student-pool-counter" style="font-size:.8125rem;padding:.1rem .5rem;border-radius:999px;background:var(--gray-200);color:var(--gray-600);font-weight:600">
          ${distributed} / ${groupPoolHours} h
        </span>
      </summary>
      <div style="padding:.5rem .875rem .875rem">
        ${rows}
        <div style="display:flex;gap:.5rem;margin-top:.625rem;flex-wrap:wrap">
          ${writeOnly(`<button class="btn btn-sm btn-primary" id="btn-save-student-pool" disabled>Speichern</button>
          ${hasStudentOverride ? `<button class="btn btn-sm btn-secondary" id="btn-reset-student-pool">Schulstandard wiederherstellen</button>` : ""}`)}
        </div>
      </div>
    </details>`;
}

function renderFortschrittTab(hoursData: StudentHoursResponse, studentId: number): string {
  const { hours } = hoursData;
  if (hours.length === 0) {
    return `<p style="color:#6b7280;font-size:.9rem;padding:.5rem 0">Noch keine Stunden erfasst.</p>`;
  }

  return `${hours.map(h => renderProgressBar(h)).join("")}${renderPoolEditor(hoursData, studentId)}`;
}

let _lastHoursData: StudentHoursResponse | null = null;

async function refreshFortschritt(studentId: number) {
  const fallback: StudentHoursResponse = { hours: [], groupPoolHours: null, hasStudentOverride: false };
  const hoursData = await api.students.hours(studentId).catch(() => fallback);
  _lastHoursData = hoursData;
  const tabPane = document.getElementById("dtab-fortschritt");
  if (tabPane) {
    tabPane.innerHTML = renderFortschrittTab(hoursData, studentId);
    attachFortschrittListeners(studentId);
  }
}

function attachFortschrittListeners(studentId: number) {
  const tabPane = document.getElementById("dtab-fortschritt");
  if (!tabPane) return;

  const hoursData = _lastHoursData;
  if (!hoursData || !hoursData.groupPoolHours || hoursData.groupPoolHours <= 0) return;

  const groupPoolHours = hoursData.groupPoolHours;
  const counter = tabPane.querySelector("#student-pool-counter") as HTMLElement;
  // Fehlt bei Betrachtern: die Pool-Verteilung ist dann nur ablesbar
  const saveBtn = tabPane.querySelector("#btn-save-student-pool") as HTMLButtonElement | null;

  function updateCounter() {
    const inputs = Array.from(tabPane!.querySelectorAll(".student-pool-input")) as HTMLInputElement[];
    const sum = inputs.reduce((acc, inp) => acc + (parseInt(inp.value) || 0), 0);
    const ok = sum === groupPoolHours;
    counter.textContent = `${sum} / ${groupPoolHours} h`;
    counter.style.background = ok ? "#dcfce7" : sum > groupPoolHours ? "#fee2e2" : "var(--gray-200)";
    counter.style.color = ok ? "#15803d" : sum > groupPoolHours ? "#dc2626" : "var(--gray-600)";
    if (saveBtn) saveBtn.disabled = !ok;
  }

  tabPane.querySelectorAll(".student-pool-input").forEach(inp => {
    inp.addEventListener("input", updateCounter);
  });
  updateCounter();

  saveBtn?.addEventListener("click", async () => {
    const inputs = Array.from(tabPane.querySelectorAll(".student-pool-input")) as HTMLInputElement[];
    const allocations: Record<string, number> = {};
    inputs.forEach(inp => { allocations[inp.dataset.deptId!] = parseInt(inp.value) || 0; });
    try {
      await api.students.savePoolDistribution(studentId, allocations);
      await refreshFortschritt(studentId);
    } catch (err: any) { alert(err.message); }
  });

  const resetBtn = tabPane.querySelector("#btn-reset-student-pool");
  if (resetBtn) {
    resetBtn.addEventListener("click", async () => {
      if (!confirm("Schulstandard wiederherstellen? Individuelle Pool-Verteilung wird gelöscht.")) return;
      try {
        await api.students.deletePoolDistribution(studentId);
        await refreshFortschritt(studentId);
      } catch (err: any) { alert(err.message); }
    });
  }
}

function renderPlanungTab(assignments: AdminStudentAssignment[]): string {
  if (assignments.length === 0) {
    return `<p style="color:#6b7280;font-size:.9rem;padding:.5rem 0">Noch keine Einsätze geplant.</p>`;
  }
  return assignments.map(a => {
    const dateRange = `${formatDate(a.startDate)} – ${formatDate(a.endDate)}`;
    const hoursInfo = `${a.hoursPerWeek} Std./Wo · ${a.durationWeeks} Wo.`;
    const nameLine = a.practiceName || (a.isFreeInput ? "Freier Einsatz" : "–");

    let rowClass = "assignment-row";
    let extraStyle = "";
    if (a.isCurrent) {
      rowClass += " assignment-current";
      extraStyle = `border-left: 3px solid ${a.departmentColor || "#6366f1"}; background: ${hexToRgba(a.departmentColor || "#6366f1", 0.06)};`;
    } else if (!a.isUpcoming) {
      rowClass += " assignment-past";
    }

    return `
      <div class="${rowClass}" style="${extraStyle}">
        <div style="display:flex;align-items:center;gap:.5rem;flex-wrap:wrap;margin-bottom:.2rem">
          <span class="badge" style="background:${a.departmentColor || "#e5e7eb"};color:#fff;font-size:.75rem">${esc(a.departmentName || a.department)}</span>
          ${a.isCurrent ? `<span style="font-size:.75rem;font-weight:600;color:${a.departmentColor || "#6366f1"}">● Aktuell</span>` : ""}
          <span style="font-size:.75rem;color:#9ca3af">${esc(a.periodName)}</span>
        </div>
        <div style="font-weight:500;font-size:.9rem">${esc(nameLine)}</div>
        <div style="font-size:.8rem;color:#6b7280;margin-top:.15rem">${dateRange} · ${hoursInfo}</div>
      </div>`;
  }).join("");
}

function renderWuenscheTab(wishes: StudentPreferences, departments: Department[]): string {
  const keys = Object.keys(wishes);
  if (keys.length === 0) {
    if (!schuelerPortalEnabled) {
      const adminHint = _isSchulAdmin
        ? ` Das Portal kann unter <a id="portal-activate-link" href="#/abonnements" style="color:var(--primary)">Abonnements</a> freigeschaltet werden.`
        : "";
      return `<p style="color:#6b7280;font-size:.9rem;padding:.5rem 0">Das Schüler-Portal ist nicht aktiviert – Schüler können ihre Wünsche darüber einreichen.${adminHint}</p>`;
    }
    return `<p style="color:#6b7280;font-size:.9rem;padding:.5rem 0">Noch keine Wünsche eingetragen.</p>`;
  }
  const deptNameMap = new Map(departments.map(d => [d.id, d.nameDe]));
  return keys.map(deptId => {
    const prefs = wishes[deptId];
    const deptName = deptNameMap.get(deptId) || deptId;
    const items = prefs.map(p =>
      `<div style="display:flex;align-items:center;gap:.4rem;padding:.25rem 0;border-bottom:1px solid #f3f4f6">
        <span style="font-size:.8rem;color:#f59e0b;font-weight:600;min-width:1.5rem">★${p.priority}</span>
        <span style="font-size:.9rem">${esc(p.practiceName)}</span>
      </div>`
    ).join("");
    return `
      <div style="margin-bottom:1rem">
        <div style="font-size:.8rem;font-weight:600;color:#6b7280;text-transform:uppercase;letter-spacing:.05em;margin-bottom:.25rem">${esc(deptName)}</div>
        ${items}
      </div>`;
  }).join("");
}

function hexToRgba(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16) || 0;
  const g = parseInt(hex.slice(3, 5), 16) || 0;
  const b = parseInt(hex.slice(5, 7), 16) || 0;
  return `rgba(${r},${g},${b},${alpha})`;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
}

async function openDetail(s: Student) {
  // Daten parallel laden
  const emptyHoursData: StudentHoursResponse = { hours: [], groupPoolHours: null, hasStudentOverride: false };
  let hoursData: StudentHoursResponse = emptyHoursData;
  let assignments: AdminStudentAssignment[] = [];

  let wishes: StudentPreferences = {};
  let departments: Department[] = [];
  const emptySteckbrief: SchuelerSteckbrief = { text: null, released: false, updatedAt: null };
  let steckbrief: SchuelerSteckbrief = emptySteckbrief;

  if (planungEnabled) {
    [hoursData, assignments, wishes, departments, steckbrief] = await Promise.all([
      api.students.hours(s.id).catch(() => emptyHoursData),
      api.students.planung(s.id).catch(() => []),
      api.schuelerWuensche(s.id).catch(() => ({})),
      api.departments().catch(() => []),
      api.schuelerSteckbrief(s.id).catch(() => emptySteckbrief),
    ]) as [StudentHoursResponse, AdminStudentAssignment[], StudentPreferences, Department[], SchuelerSteckbrief];
    _lastHoursData = hoursData;
  } else if (schuelerPortalEnabled) {
    [wishes, departments, steckbrief] = await Promise.all([
      api.schuelerWuensche(s.id).catch(() => ({})),
      api.departments().catch(() => []),
      api.schuelerSteckbrief(s.id).catch(() => emptySteckbrief),
    ]) as [StudentPreferences, Department[], SchuelerSteckbrief];
  } else {
    steckbrief = await api.schuelerSteckbrief(s.id).catch(() => emptySteckbrief);
  }

  const classOpts = classes.map(c => `<option value="${c.id}" ${s.classId === c.id ? "selected" : ""}>${esc(c.name)}</option>`).join("");
  const geoStatusHtml = s.latitude && s.longitude
    ? `<span style="color:var(--green-700)">&#10003; (${s.latitude.toFixed(4)}, ${s.longitude.toFixed(4)})</span>`
    : `<span style="color:var(--red-700)">&#10007; Nicht ermittelt</span>`;

  const loginHtml = schuelerPortalEnabled && _isSchulAdmin ? `
    <div style="margin-top:1.25rem;padding-top:1rem;border-top:1px solid #e5e7eb">
      <div style="display:flex;align-items:center;gap:.75rem;flex-wrap:wrap">
        <span style="font-size:.9rem;color:#374151">
          <strong>Schüler-Login:</strong>
          ${s.loginEnabled
            ? `<span style="color:#059669">✓ Aktiv</span>`
            : `<span style="color:#9ca3af">Nicht aktiviert</span>`}
        </span>
        ${s.loginEnabled
          ? `<button class="btn btn-secondary" id="d-reset-pw" style="font-size:.8rem;padding:.25rem .75rem">Passwort zurücksetzen</button>`
          : ""}
      </div>
    </div>` : "";

  const showWuenscheTab = planungEnabled || schuelerPortalEnabled;
  const tabBar = `
    <div class="detail-tabs">
      <button class="detail-tab active" data-tab="daten">Daten</button>
      <button class="detail-tab" data-tab="steckbrief">Steckbrief</button>
      <button class="detail-tab" data-tab="dokumente">Dokumente</button>
      ${planungEnabled ? `
      <button class="detail-tab" data-tab="planung">Planung</button>
      <button class="detail-tab" data-tab="fortschritt">Fortschritt</button>
      ` : ""}
      ${showWuenscheTab ? `<button class="detail-tab" data-tab="wuensche">Wünsche</button>` : ""}
      ${stundenplanEnabled ? `<button class="detail-tab" data-tab="fehlzeiten">Fehlzeiten</button>` : ""}
    </div>`;

  const fmtDate = (iso: string | null) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" }); }
    catch { return iso; }
  };
  const steckbriefTabHtml = (() => {
    const hasText = !!(steckbrief.text && steckbrief.text.trim());
    if (!hasText) {
      return `<div class="empty-state" style="padding:2rem 1rem;color:var(--gray-600)">Noch kein Steckbrief vorhanden.</div>`;
    }
    const badge = steckbrief.released
      ? `<span style="display:inline-block;padding:.2rem .5rem;border-radius:.25rem;background:#d1fae5;color:#065f46;font-size:.75rem;font-weight:500">Für Praktikumsstellen freigegeben</span>`
      : `<span style="display:inline-block;padding:.2rem .5rem;border-radius:.25rem;background:var(--gray-100);color:var(--gray-700);font-size:.75rem;font-weight:500">Nicht freigegeben</span>`;
    return `
      <div style="display:flex;justify-content:space-between;align-items:center;gap:1rem;margin-bottom:.75rem;flex-wrap:wrap">
        ${badge}
        <span style="font-size:.8rem;color:var(--gray-600)">Zuletzt aktualisiert: ${esc(fmtDate(steckbrief.updatedAt))}</span>
      </div>
      <div style="white-space:pre-wrap;padding:.75rem;border:1px solid var(--gray-200);border-radius:.375rem;background:var(--gray-50);font-size:.95rem;line-height:1.5">${esc(steckbrief.text!)}</div>
    `;
  })();

  const datenTabHtml = `
    <div class="form-grid">
      <div class="form-group"><label>Vorname *</label><input id="d-fn" value="${esc(s.firstName || "")}" /></div>
      <div class="form-group"><label>Nachname *</label><input id="d-ln" value="${esc(s.lastName || "")}" /></div>
      <div class="form-group"><label>Straße</label><input id="d-street" value="${esc(s.street || "")}" /></div>
      <div class="form-group"><label>Hausnr.</label><input id="d-hno" value="${esc(s.houseNo || "")}" /></div>
      <div class="form-group"><label>PLZ</label><input id="d-plz" value="${esc(s.plz || "")}" /></div>
      <div class="form-group"><label>Ort</label><input id="d-ort" value="${esc(s.ort || "")}" /></div>
      <div class="form-group col-span-2"><label>Koordinaten</label><div style="display:flex;align-items:center;gap:.5rem"><div id="d-geo-status" style="font-size:.8125rem">${geoStatusHtml}</div><button type="button" class="btn btn-sm btn-secondary" id="d-geocode" style="white-space:nowrap">Ermitteln</button></div></div>
      <div class="form-group"><label>E-Mail</label><input id="d-email" value="${esc(s.email || "")}"${s.loginEnabled ? ' disabled title="E-Mail kann nicht geändert werden, solange der Portal-Login aktiv ist" style="background:var(--gray-50);cursor:not-allowed"' : ''} />${s.loginEnabled ? '<small style="color:var(--gray-500)">Benutzername im Schüler-Portal – bitte Login erst deaktivieren.</small>' : ''}</div>
      <div class="form-group"><label>Telefon</label><input id="d-phone" value="${esc(s.phone || "")}" /></div>
      <div class="form-group"><label>Klasse</label><select id="d-class"><option value="">– Keine –</option>${classOpts}</select></div>
      ${kreisverbandFieldHtml("d-kreisverband", s.drkKreisverbandId)}
      <div class="form-group" style="justify-content:flex-end"><label class="checkbox-label"><input type="checkbox" id="d-car" ${s.hasCar ? "checked" : ""} /> Auto vorhanden</label></div>
    </div>
    ${loginHtml}`;

  const planungTabHtml = planungEnabled
    ? renderPlanungTab(assignments)
    : "";

  const fortschrittTabHtml = planungEnabled ? renderFortschrittTab(hoursData, s.id) : "";

  const wuenscheTabHtml = showWuenscheTab
    ? renderWuenscheTab(wishes, departments)
    : "";

  openModal(`${esc(s.firstName)} ${esc(s.lastName)}`, `
    ${tabBar}
    <div class="tab-content-wrap">
      <div class="tab-pane" id="dtab-daten">
        ${datenTabHtml}
      </div>
      <div class="tab-pane" id="dtab-steckbrief">${steckbriefTabHtml}</div>
      <div class="tab-pane" id="dtab-dokumente">${renderDocumentsTab()}</div>
      ${planungEnabled ? `
      <div class="tab-pane" id="dtab-planung">${planungTabHtml}</div>
      <div class="tab-pane" id="dtab-fortschritt">${fortschrittTabHtml}</div>
      ` : ""}
      ${showWuenscheTab ? `<div class="tab-pane" id="dtab-wuensche">${wuenscheTabHtml}</div>` : ""}
      ${stundenplanEnabled ? `<div class="tab-pane" id="dtab-fehlzeiten"><div class="spinner"></div></div>` : ""}
    </div>
    <div class="form-actions" style="margin-top:1.25rem">
      <button class="btn btn-secondary" id="d-close">Schließen</button>
      ${writeOnly(`<button class="btn btn-primary" id="d-save" style="display:none">Speichern</button>`)}
    </div>`, { wide: true, fixedSize: true });

  const mb = modalBody();
  // Betrachter sehen die Detailansicht schreibgeschützt
  lockInputs(mb);
  let geoLat: number | null = s.latitude ?? null;
  let geoLng: number | null = s.longitude ?? null;

  // Pool-Target-Listener nach Modal-Öffnen einhängen
  if (planungEnabled) {
    attachFortschrittListeners(s.id);
  }

  // Tab-Switching
  const portalLink = mb.querySelector<HTMLAnchorElement>("#portal-activate-link");
  if (portalLink) portalLink.addEventListener("click", closeModal);

  const tabs = mb.querySelectorAll<HTMLButtonElement>(".detail-tab");
  const panes = mb.querySelectorAll<HTMLElement>(".tab-pane");
  // Fehlt bei Betrachtern, die Detailansicht ist dann rein lesend
  const saveBtn = mb.querySelector<HTMLButtonElement>("#d-save");

  // Ersten Tab (Daten) initial anzeigen + Speichern-Button sichtbar machen
  panes[0].style.display = "block";
  if (saveBtn) saveBtn.style.display = "";

  tabs.forEach(tab => {
    tab.addEventListener("click", () => {
      tabs.forEach(t => t.classList.remove("active"));
      panes.forEach(p => { p.classList.remove("active"); p.style.display = "none"; });
      tab.classList.add("active");
      const target = mb.querySelector<HTMLElement>(`#dtab-${tab.dataset.tab}`)!;
      target.classList.add("active");
      target.style.display = "block";
      if (saveBtn) saveBtn.style.display = tab.dataset.tab === "daten" ? "" : "none";
      // Erst beim Öffnen laden: Der Nachweis rechnet über den ganzen Kursverlauf und
      // gehört nicht in jeden Aufruf der Detailansicht.
      if (tab.dataset.tab === "fehlzeiten") loadFehlzeiten(s.id, target);
    });
  });

  // Dokumente-Listener einmalig einhängen (lädt die Dokumentenliste sofort)
  const docsTab = mb.querySelector<HTMLElement>("#dtab-dokumente")!;
  attachDocumentsListeners({
    list: () => api.studentDocuments.list(s.id),
    upload: (file) => api.studentDocuments.upload(s.id, file),
    download: (docId, name) => api.studentDocuments.download(s.id, docId, name),
    delete: (docId) => api.studentDocuments.delete(s.id, docId),
    rename: (docId, name) => api.studentDocuments.rename(s.id, docId, name),
    merge: (docIds, filename) => api.studentDocuments.merge(s.id, docIds, filename),
  }, docsTab);

  // Geocode-Button
  mb.querySelector("#d-geocode")!.addEventListener("click", async () => {
    const btn = mb.querySelector("#d-geocode") as HTMLButtonElement;
    const geoStatus = mb.querySelector("#d-geo-status")!;
    btn.disabled = true; btn.textContent = "Suche…";
    geoStatus.innerHTML = `<span style="color:var(--gray-500)">Adresse wird gesucht…</span>`;
    const geo = await geocodeAddressStudent(dval("#d-street"), dval("#d-hno"), dval("#d-plz"), dval("#d-ort"));
    if (geo) {
      geoLat = geo.lat; geoLng = geo.lng;
      geoStatus.innerHTML = `<span style="color:var(--green-700)">&#10003; (${geo.lat.toFixed(4)}, ${geo.lng.toFixed(4)})</span>`;
    } else {
      geoLat = null; geoLng = null;
      geoStatus.innerHTML = `<span style="color:var(--red-700)">&#10007; Adresse nicht gefunden</span>`;
    }
    btn.disabled = false; btn.textContent = "Ermitteln";
  });

  // Schließen
  mb.querySelector("#d-close")!.addEventListener("click", closeModal);

  // Speichern
  mb.querySelector("#d-save")?.addEventListener("click", async () => {
    const curStreet = dval("#d-street"), curHno = dval("#d-hno"), curPlz = dval("#d-plz"), curOrt = dval("#d-ort");
    const addrChanged = curStreet !== (s.street || "") || curHno !== (s.houseNo || "") || curPlz !== (s.plz || "") || curOrt !== (s.ort || "");
    const saveBtn = mb.querySelector("#d-save") as HTMLButtonElement;
    if (!geoLat || addrChanged) {
      saveBtn.disabled = true; saveBtn.textContent = "Koordinaten…";
      const geo = await geocodeAddressStudent(curStreet, curHno, curPlz, curOrt);
      if (geo) { geoLat = geo.lat; geoLng = geo.lng; }
      else if (addrChanged) { geoLat = null; geoLng = null; }
      saveBtn.disabled = false; saveBtn.textContent = "Speichern";
    }
    const data = {
      firstName: (mb.querySelector("#d-fn") as HTMLInputElement).value.trim(),
      lastName: (mb.querySelector("#d-ln") as HTMLInputElement).value.trim(),
      street: curStreet, houseNo: curHno, plz: curPlz, ort: curOrt,
      email: dval("#d-email"), phone: dval("#d-phone"),
      classId: parseInt((mb.querySelector("#d-class") as HTMLSelectElement).value) || null,
      hasCar: (mb.querySelector("#d-car") as HTMLInputElement).checked,
      drkKreisverbandId: drkKreisverbandEnabled ? (parseInt((mb.querySelector("#d-kreisverband") as HTMLSelectElement)?.value) || null) : undefined,
      latitude: geoLat, longitude: geoLng,
    };
    if (!data.firstName || !data.lastName) { alert("Vor- und Nachname sind erforderlich."); return; }
    saveBtn.disabled = true; saveBtn.textContent = "Speichern…";
    try {
      await api.students.update(s.id, data);
      closeModal();
      // Frisch laden (damit className aus JOIN korrekt bleibt)
      await load();
      if (currentEl) renderTable(currentEl);
    } catch (err: any) {
      alert("Fehler: " + err.message);
      saveBtn.disabled = false; saveBtn.textContent = "Speichern";
    }
  });

  // Login-Reset
  mb.querySelector("#d-reset-pw")?.addEventListener("click", () => {
    closeModal();
    (window as any).__sResetPw(s.id);
  });

  function dval(sel: string) { return (mb.querySelector(sel) as HTMLInputElement)?.value || ""; }
}

function openForm(s?: Student) {
  const isEdit = !!s;
  const classOpts = classes.map(c => `<option value="${c.id}" ${s?.classId === c.id ? "selected" : ""}>${esc(c.name)}</option>`).join("");
  const geoStatusHtml = s?.latitude && s?.longitude
    ? `<span style="color:var(--green-700)">&#10003; (${s.latitude.toFixed(4)}, ${s.longitude.toFixed(4)})</span>`
    : `<span style="color:var(--red-700)">&#10007; Nicht ermittelt</span>`;
  openModal(isEdit ? "Schüler bearbeiten" : "Neuer Schüler", `
    <div class="form-grid">
      <div class="form-group"><label>Vorname *</label><input id="f-fn" value="${esc(s?.firstName || "")}" /></div>
      <div class="form-group"><label>Nachname *</label><input id="f-ln" value="${esc(s?.lastName || "")}" /></div>
      <div class="form-group"><label>Straße</label><input id="f-street" value="${esc(s?.street || "")}" /></div>
      <div class="form-group"><label>Hausnr.</label><input id="f-hno" value="${esc(s?.houseNo || "")}" /></div>
      <div class="form-group"><label>PLZ</label><input id="f-plz" value="${esc(s?.plz || "")}" /></div>
      <div class="form-group"><label>Ort</label><input id="f-ort" value="${esc(s?.ort || "")}" /></div>
      <div class="form-group col-span-2"><label>Koordinaten</label><div style="display:flex;align-items:center;gap:.5rem"><div id="f-geo-status" style="font-size:.8125rem">${geoStatusHtml}</div>${writeOnly(`<button type="button" class="btn btn-sm btn-secondary" id="f-geocode" style="white-space:nowrap">Ermitteln</button>`)}</div></div>
      <div class="form-group"><label>E-Mail</label><input id="f-email" value="${esc(s?.email || "")}"${s?.loginEnabled ? ' disabled title="E-Mail kann nicht geändert werden, solange der Portal-Login aktiv ist" style="background:var(--gray-50);cursor:not-allowed"' : ''} />${s?.loginEnabled ? '<small style="color:var(--gray-500)">Benutzername im Schüler-Portal – bitte Login erst deaktivieren.</small>' : ''}</div>
      <div class="form-group"><label>Telefon</label><input id="f-phone" value="${esc(s?.phone || "")}" /></div>
      <div class="form-group"><label>Klasse</label><select id="f-class"><option value="">– Keine –</option>${classOpts}</select></div>
      ${kreisverbandFieldHtml("f-kreisverband", s?.drkKreisverbandId)}
      <div class="form-group" style="justify-content:flex-end"><label class="checkbox-label"><input type="checkbox" id="f-car" ${s?.hasCar ? "checked" : ""} /> Auto vorhanden</label></div>
    </div>
    <div class="form-actions"><button class="btn btn-secondary" id="f-cancel">${canWrite() ? "Abbrechen" : "Schließen"}</button>${writeOnly(`<button class="btn btn-primary" id="f-save">Speichern</button>`)}</div>`, { wide: true });

  const mb = modalBody();
  // Betrachter sehen die Maske schreibgeschützt
  lockInputs(mb);
  let geoLat: number | null = s?.latitude ?? null;
  let geoLng: number | null = s?.longitude ?? null;

  mb.querySelector("#f-geocode")?.addEventListener("click", async () => {
    const btn = mb.querySelector("#f-geocode") as HTMLButtonElement;
    const geoStatus = mb.querySelector("#f-geo-status")!;
    btn.disabled = true; btn.textContent = "Suche…";
    geoStatus.innerHTML = `<span style="color:var(--gray-500)">Adresse wird gesucht…</span>`;
    const geo = await geocodeAddressStudent(val("#f-street"), val("#f-hno"), val("#f-plz"), val("#f-ort"));
    if (geo) {
      geoLat = geo.lat; geoLng = geo.lng;
      geoStatus.innerHTML = `<span style="color:var(--green-700)">&#10003; (${geo.lat.toFixed(4)}, ${geo.lng.toFixed(4)})</span>`;
    } else {
      geoLat = null; geoLng = null;
      geoStatus.innerHTML = `<span style="color:var(--red-700)">&#10007; Adresse nicht gefunden</span>`;
    }
    btn.disabled = false; btn.textContent = "Ermitteln";
  });

  mb.querySelector("#f-cancel")!.addEventListener("click", closeModal);
  mb.querySelector("#f-save")?.addEventListener("click", async () => {
    const curStreet = val("#f-street"), curHno = val("#f-hno"), curPlz = val("#f-plz"), curOrt = val("#f-ort");
    const addrChanged = curStreet !== (s?.street || "") || curHno !== (s?.houseNo || "") || curPlz !== (s?.plz || "") || curOrt !== (s?.ort || "");
    if (!geoLat || addrChanged) {
      const saveBtn = mb.querySelector("#f-save") as HTMLButtonElement;
      saveBtn.disabled = true; saveBtn.textContent = "Koordinaten werden ermittelt…";
      const geo = await geocodeAddressStudent(curStreet, curHno, curPlz, curOrt);
      if (geo) { geoLat = geo.lat; geoLng = geo.lng; }
      else if (addrChanged) { geoLat = null; geoLng = null; }
      saveBtn.disabled = false; saveBtn.textContent = "Speichern";
    }
    const data = {
      firstName: (mb.querySelector("#f-fn") as HTMLInputElement).value.trim(),
      lastName: (mb.querySelector("#f-ln") as HTMLInputElement).value.trim(),
      street: curStreet, houseNo: curHno, plz: curPlz, ort: curOrt,
      email: val("#f-email"), phone: val("#f-phone"),
      classId: (mb.querySelector("#f-class") as HTMLSelectElement).value || null,
      hasCar: (mb.querySelector("#f-car") as HTMLInputElement).checked,
      drkKreisverbandId: drkKreisverbandEnabled ? (parseInt((mb.querySelector("#f-kreisverband") as HTMLSelectElement)?.value) || null) : undefined,
      latitude: geoLat,
      longitude: geoLng,
    };
    if (!data.firstName || !data.lastName) return;
    const saveBtn = mb.querySelector("#f-save") as HTMLButtonElement;
    saveBtn.disabled = true; saveBtn.textContent = "Speichern…";
    try {
      if (isEdit) await api.students.update(s!.id, data); else await api.students.create(data);
    } catch (err: any) {
      // z.B. Portal-Zugang ließ sich nicht aktualisieren – dann wurde nichts gespeichert
      alert("Fehler: " + err.message);
      saveBtn.disabled = false; saveBtn.textContent = "Speichern";
      return;
    }
    closeModal(); await load(); if (currentEl) renderTable(currentEl);
  });

  function val(sel: string) { return (mb.querySelector(sel) as HTMLInputElement)?.value || ""; }
}

/**
 * fn für alle Einträge ausführen, höchstens `limit` gleichzeitig; Ergebnisse in
 * Eingangsreihenfolge. Massenaktionen schickten sonst hunderte Anfragen auf einmal
 * an Backend und Keycloak.
 */
async function runLimited<T, R>(items: T[], limit: number, fn: (item: T) => Promise<R>): Promise<R[]> {
  const results: R[] = new Array(items.length);
  let next = 0;
  const worker = async () => {
    while (next < items.length) {
      const i = next++;
      results[i] = await fn(items[i]);
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
  return results;
}

const BULK_PARALLEL = 3;

async function handlePortalBulkEnable(el: HTMLElement) {
  const selectedIdStrs = [...el.querySelectorAll<HTMLInputElement>(".row-sel:checked")].map(b => b.dataset.id!);
  if (!selectedIdStrs.length) return;
  const selected = students.filter(s => selectedIdStrs.includes(String(s.id)));
  const toEnable = selected.filter(s => !s.loginEnabled && s.email);
  const noEmail = selected.filter(s => !s.loginEnabled && !s.email);
  const alreadyEnabled = selected.filter(s => s.loginEnabled);
  if (toEnable.length === 0) {
    if (alreadyEnabled.length === selectedIdStrs.length) {
      alert("Alle ausgewählten Schüler haben bereits einen aktiven Login.");
    } else {
      alert(`${noEmail.length} Schüler ohne E-Mail können nicht aktiviert werden.`);
    }
    return;
  }
  let msg = `Schüler-Portal für ${toEnable.length} Schüler aktivieren?`;
  if (noEmail.length > 0) msg += `\n\n${noEmail.length} Schüler ohne E-Mail werden übersprungen.`;
  if (!confirm(msg)) return;
  const total = toEnable.length;
  let completed = 0;
  openModal("Schüler-Portal wird aktiviert…", `
    <div style="text-align:center;padding:2rem 1rem">
      <div class="spinner" style="margin:0 auto 1.5rem"></div>
      <p style="color:#6b7280;font-size:.95rem" id="bulk-progress">0 von ${total} aktiviert (0%)</p>
    </div>`);
  const results = await runLimited(toEnable, BULK_PARALLEL, async s => {
    const name = `${s.firstName} ${s.lastName}`;
    try {
      const r = await api.students.toggleLogin(s.id, true, s.email!);
      const queued = r.mode === "invite" && r.emailQueued === true;
      const invited = queued || (r.mode === "invite" && r.emailSent === true);
      return { name, ok: true, password: r.password || "", invited, queued, error: "" };
    } catch (err: any) {
      // Ein einzelner Fehlschlag, etwa eine schon vergebene E-Mail-Adresse, darf die
      // übrigen Schüler nicht aufhalten und den Fortschrittsdialog nicht hängen lassen.
      return { name, ok: false, password: "", invited: false, queued: false, error: err?.message || "Unbekannter Fehler" };
    } finally {
      completed++;
      const progressEl = document.getElementById("bulk-progress");
      if (progressEl) progressEl.textContent = `${completed} von ${total} aktiviert (${Math.round(completed / total * 100)}%)`;
    }
  });
  try { await load(); } catch { /* Liste bleibt auf altem Stand, das Ergebnis zeigen wir trotzdem */ }
  if (currentEl) renderTable(currentEl);
  const failed = results.filter(r => !r.ok);
  const okCount = total - failed.length;
  const failedHtml = failed.length > 0
    ? `<div class="alert" style="background:#fef2f2;border:1px solid #fca5a5;border-radius:6px;padding:.75rem 1rem;font-size:.875rem;margin-bottom:1rem">
         <p style="margin:0 0 .5rem">Bei ${failed.length} Schüler${failed.length !== 1 ? "n" : ""} ließ sich der Login nicht aktivieren:</p>
         <ul style="margin:0;padding-left:1.25rem">${failed.map(f => `<li><strong>${esc(f.name)}</strong>: ${esc(f.error)}</li>`).join("")}</ul>
       </div>`
    : "";
  // Im Einladungsmodus liefert nur der Fehlerfall ein Kennwort. Die Tabelle listet
  // deshalb genau die Schüler, um die sich die Verwaltung noch kümmern muss.
  const withPw = results.filter(r => r.password);
  const invitedCount = results.filter(r => r.invited).length;
  // Eingereihte Einladungen verschickt der Mail-Worker gedrosselt im Hintergrund.
  const queuedCount = results.filter(r => r.queued).length;
  const mb = modalBody();
  document.getElementById("modal-title")!.textContent = withPw.length > 0
    ? "Logins aktiviert, Kennwörter"
    : okCount > 0 ? "Logins aktiviert" : "Logins nicht aktiviert";
  if (withPw.length > 0) {
    const rows = withPw.map(r => `<tr><td style="padding:.25rem .5rem">${esc(r.name)}</td><td style="padding:.25rem .5rem;font-family:monospace;font-weight:600">${esc(r.password)}</td></tr>`).join("");
    const intro = invitedCount > 0
      ? `<div class="alert" style="background:#fef2f2;border:1px solid #fca5a5;border-radius:6px;padding:.75rem 1rem;font-size:.875rem;margin-bottom:1rem">
           ${invitedCount} von ${okCount} Schülern ${queuedCount > 0 ? "bekommen in den nächsten Minuten" : "haben"} eine Einladung per E-Mail${queuedCount > 0 ? "" : " bekommen"}.
           Bei den folgenden ${withPw.length} konnte keine Einladung versendet werden.
           Bitte gib diesen Schülern das Erstkennwort persönlich weiter.
         </div>`
      : `<p style="font-size:.9rem;color:#374151;margin-bottom:1rem">Diese Kennwörter werden <strong>nur einmal</strong> angezeigt.</p>`;
    mb.innerHTML = `
      ${failedHtml}
      ${intro}
      <div style="overflow:auto;max-height:300px">
        <table style="width:100%;border-collapse:collapse;font-size:.875rem">
          <thead><tr><th style="text-align:left;padding:.25rem .5rem;border-bottom:1px solid #e5e7eb">Schüler</th><th style="text-align:left;padding:.25rem .5rem;border-bottom:1px solid #e5e7eb">Erstkennwort</th></tr></thead>
          <tbody>${rows}</tbody>
        </table>
      </div>
      <div class="form-actions">
        <button class="btn btn-secondary" id="pw-copy-all">Alle kopieren</button>
        <button class="btn btn-primary" id="pw-close">Schließen</button>
      </div>`;
    const copyText = withPw.map(r => `${r.name}\t${r.password}`).join("\n");
    mb.querySelector("#pw-copy-all")!.addEventListener("click", function () {
      navigator.clipboard.writeText(copyText).then(() => {
        (this as HTMLButtonElement).textContent = "✓ Kopiert";
        setTimeout(() => { (this as HTMLButtonElement).textContent = "Alle kopieren"; }, 2000);
      }).catch(() => {});
    });
    mb.querySelector("#pw-close")!.addEventListener("click", closeModal);
  } else {
    mb.innerHTML = `
      ${failedHtml}
      ${okCount > 0 ? `<p style="font-size:.9rem;color:#374151;margin-bottom:1rem">${okCount} Login${okCount !== 1 ? "s" : ""} erfolgreich aktiviert.</p>` : ""}
      ${invitedCount > 0 ? `<p style="font-size:.875rem;color:#6b7280;margin-bottom:1rem">
        ${queuedCount > 0
          ? `Die Einladungen an ${invitedCount} Schüler werden im Hintergrund per E-Mail verschickt. Das kann einige Minuten dauern, du musst das Fenster dafür nicht offen lassen. Die Schüler vergeben ihr Passwort selbst.`
          : `${invitedCount} Schüler haben eine Einladung per E-Mail bekommen und vergeben ihr Passwort selbst.`}
      </p>` : ""}
      <div class="form-actions"><button class="btn btn-primary" id="pw-close">Schließen</button></div>`;
    mb.querySelector("#pw-close")!.addEventListener("click", closeModal);
  }
}

async function handlePortalBulkDisable(el: HTMLElement) {
  const selectedIdStrs = [...el.querySelectorAll<HTMLInputElement>(".row-sel:checked")].map(b => b.dataset.id!);
  if (!selectedIdStrs.length) return;
  const toDisable = students.filter(s => selectedIdStrs.includes(String(s.id)) && s.loginEnabled);
  if (toDisable.length === 0) {
    alert("Kein ausgewählter Schüler hat einen aktiven Login.");
    return;
  }
  if (!confirm(`Schüler-Portal für ${toDisable.length} Schüler deaktivieren?`)) return;
  const errors = await runLimited(toDisable, BULK_PARALLEL, async s => {
    try { await api.students.toggleLogin(s.id, false); return ""; }
    catch (err: any) { return `${s.firstName} ${s.lastName}: ${err?.message || "Unbekannter Fehler"}`; }
  });
  try { await load(); } catch { /* Liste bleibt auf altem Stand */ }
  if (currentEl) renderTable(currentEl);
  const failed = errors.filter(Boolean);
  if (failed.length > 0) {
    alert(`Bei ${failed.length} Schüler${failed.length !== 1 ? "n" : ""} ließ sich der Login nicht deaktivieren:\n\n${failed.join("\n")}`);
  }
}

export async function render(el: HTMLElement) {
  currentEl = el;
  searchQuery = "";
  const portalDropdownHtml = (schuelerPortalEnabled && _isSchulAdmin) ? `
    <div class="dropdown-check" id="dd-portal">
      <button class="btn btn-secondary btn-sm dropdown-check-btn" id="btn-portal-sel" disabled>Portal</button>
      <div class="dropdown-check-panel">
        <div class="dropdown-check-item" id="portal-enable">Schüler-Portal aktivieren</div>
        <div class="dropdown-check-item" id="portal-disable">Schüler-Portal deaktivieren</div>
      </div>
    </div>` : "";
  el.innerHTML = `<div class="page-header"><h1>Schüler</h1><div style="display:flex;gap:.5rem">${writeOnly(`<button class="btn btn-secondary" id="btn-csv">CSV Import</button><button class="btn btn-primary" id="btn-add">+ Neuer Schüler</button>`)}</div></div>
    ${readOnlyNotice()}
    <div class="filter-bar"><select id="filter-class" data-readable="true"><option value="">Alle Klassen</option></select><input type="search" id="search-query" data-readable="true" placeholder="Suchen …" />${writeOnly(`<button class="btn btn-danger btn-sm" id="btn-del-sel-s" disabled>Auswahl löschen</button>`)}${portalDropdownHtml}</div>
    <div class="card table-wrap"><table><thead><tr></tr></thead><tbody></tbody></table></div>`;
  el.querySelector("#btn-add")?.addEventListener("click", () => openForm());
  el.querySelector("#btn-csv")?.addEventListener("click", () => {
    openCsvImportModal({
      title: "CSV Import: Schüler",
      duplicateHint: "Bereits vorhandene Schüler (gleicher Vor-/Nachname + Klasse) werden übersprungen.",
      fieldDescriptions: [
        { field: "Vorname",        hint: "Pflichtfeld" },
        { field: "Nachname",       hint: "Pflichtfeld" },
        { field: "E-Mail",         hint: "Optional" },
        { field: "Telefon",        hint: "Optional" },
        { field: "Straße",         hint: "Optional" },
        { field: "Hausnummer",     hint: "Optional" },
        { field: "PLZ",            hint: "Optional" },
        { field: "Ort",            hint: "Optional" },
        { field: "Klasse",         hint: "Optional, muss exakt einem vorhandenen Klassennamen entsprechen" },
        { field: "Auto vorhanden", hint: "Optional, \"ja\" eintragen, wenn ein Auto vorhanden ist; sonst leer lassen" },
        ...(drkKreisverbandEnabled ? [{ field: "DRK Kreisverband", hint: "Optional, muss exakt einem vorhandenen Kreisverband entsprechen; unbekannte Werte werden leer gelassen" }] : []),
      ],
      onDownloadTemplate: () => api.students.csvTemplate(),
      onImport: (file) => api.students.csvImport(file),
      onComplete: async () => { await load(); if (currentEl) renderTable(currentEl); },
    });
  });
  await load();
  const sel = el.querySelector("#filter-class") as HTMLSelectElement;
  sel.innerHTML = `<option value="">Alle Klassen</option>` + classes.map(c => `<option value="${c.id}">${esc(c.name)}</option>`).join("");
  sel.addEventListener("change", async () => { filterClass = sel.value ? +sel.value : undefined; await load(); renderTable(el); });
  const searchInput = el.querySelector("#search-query") as HTMLInputElement;
  searchInput.addEventListener("input", () => { searchQuery = searchInput.value; renderTable(el); });
  renderTable(el);

  // Auswahl löschen – einmaliges Wiring (Button ist statisch in filter-bar)
  el.querySelector<HTMLButtonElement>("#btn-del-sel-s")?.addEventListener("click", async () => {
    const ids = [...el.querySelectorAll<HTMLInputElement>(".row-sel:checked")].map(b => +b.dataset.id!);
    if (!ids.length) return;
    if (!confirm(`${ids.length} Schüler löschen?`)) return;
    try { await Promise.all(ids.map(id => api.students.delete(id))); await load(); if (currentEl) renderTable(currentEl); }
    catch (err: any) { alert("Fehler: " + err.message); }
  });

  if (schuelerPortalEnabled && _isSchulAdmin) {
    const ddPortal = el.querySelector("#dd-portal")!;
    el.querySelector("#btn-portal-sel")!.addEventListener("click", () => ddPortal.classList.toggle("open"));
    el.addEventListener("click", (e) => { if (!ddPortal.contains(e.target as Node)) ddPortal.classList.remove("open"); });
    el.querySelector("#portal-enable")!.addEventListener("click", async () => {
      ddPortal.classList.remove("open");
      await handlePortalBulkEnable(el);
    });
    el.querySelector("#portal-disable")!.addEventListener("click", async () => {
      ddPortal.classList.remove("open");
      await handlePortalBulkDisable(el);
    });
  }
}

function studentGeoIcon(s: Student): string {
  if (!s.plz && !s.ort) return "";
  return s.latitude && s.longitude
    ? `<span class="badge badge-green" title="Koordinaten vorhanden">&#10003;</span>`
    : `<span class="badge" style="background:var(--red-50);color:var(--red-700)" title="Keine Koordinaten">&#10007;</span>`;
}

async function geocodeAddressStudent(street: string, houseNo: string, plz: string, ort: string): Promise<{ lat: number; lng: number } | null> {
  const parts = [street, houseNo, plz, ort].filter(Boolean);
  if (parts.length < 2) return null;
  try {
    const r = await api.geocode.lookup(street, houseNo, plz, ort);
    if (r.lat != null && r.lng != null) return { lat: r.lat, lng: r.lng };
  } catch {
    // Die Wohnanschrift gehört nicht ins Log.
    console.warn("[Meduvio] Geocoding fehlgeschlagen.");
  }
  return null;
}

const ABSENCE_KIND_LABEL: Record<string, string> = {
  gemeldet: "Gemeldet, noch nicht eingestuft",
  entschuldigt: "Entschuldigt",
  unentschuldigt: "Unentschuldigt",
  beurlaubt: "Beurlaubt",
};

/**
 * Fehlzeiten-Nachweis eines Schülers: versäumte gegen tatsächlich gehaltene Stunden.
 *
 * Der Bezug ist bewusst "gehalten" und nicht "geplant". Ausgefallener Unterricht zählt auf
 * keiner der beiden Seiten mit, und genau darauf kommt es an: Nachgewiesen werden muss,
 * was stattgefunden hat.
 *
 * Rein lesend. Bearbeitet werden Meldungen dort, wo man sie im Alltag braucht, nämlich in
 * der Anwesenheitsansicht des Stundenplans.
 */
async function loadFehlzeiten(studentId: number, host: HTMLElement) {
  try {
    const report = await api.students.absences(studentId);
    const fmt = (iso: string) => new Date(iso).toLocaleDateString("de-DE");
    const period = (a: any) => {
      const days = a.endDate !== a.startDate ? `${fmt(a.startDate)} bis ${fmt(a.endDate)}` : fmt(a.startDate);
      return `${days} · ${a.startTime ? `${a.startTime} bis ${a.endTime}` : "ganztags"}`;
    };
    const share = report.heldHours > 0 ? ` von ${report.heldHours} gehaltenen Stunden` : "";

    host.innerHTML = `
      <div style="margin-bottom:1rem">
        <div style="font-size:1.5rem;font-weight:700">${esc(String(report.absentHours))} Stunden versäumt</div>
        <div style="font-size:.85rem;color:var(--gray-600)">seit Kursbeginn${esc(share)}</div>
      </div>
      ${report.absences.length === 0
        ? `<div class="empty-state" style="padding:2rem 1rem;color:var(--gray-600)">Für diesen Schüler ist nichts gemeldet.</div>`
        : `<div class="table-wrap"><table>
            <thead><tr><th>Zeitraum</th><th>Art</th><th>Attest</th><th>Quelle</th></tr></thead>
            <tbody>${report.absences.map((a: any) => `
              <tr${a.status === "aktiv" ? "" : ` style="opacity:.55"`}>
                <td>${esc(period(a))}</td>
                <td>${a.status === "aktiv" ? esc(ABSENCE_KIND_LABEL[a.kind] || a.kind) : "Zurückgenommen"}</td>
                <td>${a.attestVorgelegt ? "liegt vor" : ""}</td>
                <td>${a.source === "schueler" ? "Selbstmeldung" : "Schule"}</td>
              </tr>`).join("")}</tbody>
          </table></div>
          <p style="font-size:.8rem;color:var(--gray-600);margin-top:.75rem">
            Bearbeitet werden Meldungen im Stundenplan unter „Anwesenheit".
          </p>`}`;
  } catch (err: any) {
    host.innerHTML = `<div class="alert alert-error">Fehler beim Laden: ${esc(err.message)}</div>`;
  }
}
